import { useCallback, useEffect, useRef, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import { useNavigate } from "react-router-dom"

// Tables whose changes should trigger a refetch of the dashboard view.
// Supabase Realtime operates on base tables, not views, so we watch all four
// tables that v_dashboard_table joins and refetch whenever any of them changes.
const REALTIME_TABLES = ["property", "property_owner", "lease", "tenant"]

const COLUMNS = [
    { field: "short_name", headerName: "Nom court",      flex: 1 },
    { field: "city",       headerName: "Ville",          flex: 1 },
    { field: "owners",     headerName: "Propriétaires",  flex: 2 },
    { field: "tenants",    headerName: "Locataires",     flex: 2 },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @param {{ supabase: import("@supabase/supabase-js").SupabaseClient }} props
 *
 * `supabase` must be created once at app root via
 *   const supabase = createClient(supabaseUrl, supabaseAnonKey)
 * and passed down as a prop so that a single WebSocket connection is reused
 * across the lifetime of the application.
 */
export default function DashboardTable({ supabase }) {
    const navigate = useNavigate()

    const [rows, setRows]       = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError]     = useState(null)

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    // Using v_dashboard_table for two reasons:
    //   1. It centralises business logic (owner/tenant aggregation, active-lease
    //      filtering) in the database layer rather than duplicating it here.
    //   2. The previous inline query used COALESCE on a single JOIN path, which
    //      silently drops owners/tenants when both person and company columns are
    //      non-null. The view uses UNION ALL, which is semantically correct.
    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)

        const { data, error: sbError } = await supabase
            .from("v_dashboard_table")
            .select("id, short_name, city, owners, tenants")

        if (sbError) {
            console.error("[DashboardTable] fetch error:", sbError)
            setError(sbError.message)
        } else {
            setRows(data ?? [])
        }

        setLoading(false)
    }, [supabase])

    // -----------------------------------------------------------------------
    // Initial load
    // -----------------------------------------------------------------------
    useEffect(() => {
        fetchData()
    }, [fetchData])

    // -----------------------------------------------------------------------
    // Real-time subscription
    // -----------------------------------------------------------------------
    // We subscribe to all base tables that feed v_dashboard_table and refetch
    // whenever INSERT / UPDATE / DELETE occurs on any of them.
    //
    // A debounce (200 ms) collapses burst mutations (e.g. saving a lease + its
    // tenants in quick succession) into a single network round-trip.
    const debounceTimer = useRef(null)

    useEffect(() => {
        const channel = supabase.channel("dashboard-table-watch")

        REALTIME_TABLES.forEach((table) => {
            channel.on(
                "postgres_changes",
                { event: "*", schema: "public", table },
                () => {
                    clearTimeout(debounceTimer.current)
                    debounceTimer.current = setTimeout(fetchData, 200)
                },
            )
        })

        channel.subscribe()

        return () => {
            clearTimeout(debounceTimer.current)
            supabase.removeChannel(channel)
        }
    }, [supabase, fetchData])

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <DataGrid
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            rows={rows}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            onRowClick={({ row }) => navigate(`/property/${row.id}`)}
            getRowClassName={({ indexRelativeToCurrentPage }) =>
                indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            density="compact"
            slotProps={{
                loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                },
            }}
            // Surface fetch errors inside the grid rather than letting them
            // silently disappear. The overlay is only shown when rows is empty,
            // so existing data stays visible if a background refetch fails.
            slots={error ? {
                noRowsOverlay: () => (
                    <div style={{ padding: 16, color: "red", textAlign: "center" }}>
                        Erreur lors du chargement : {error}
                    </div>
                ),
            } : undefined}
        />
    )
}
